"use client";

import { RiPlayFill, RiStopFill } from "@remixicon/react";
import { cn } from "@workspace/ui/lib/utils";
import { memo, useCallback, useEffect, useState } from "react";
import { getArtistTopTracksAction } from "@/lib/actions/spotify/get-top-tracks.action";
import { spotifyAudioManager } from "@/lib/audio/spotify-audio-manager";

interface SpotifyPreviewPillProps {
	spotifyId: string | null | undefined;
	artistName?: string;
	className?: string;
}

/**
 * Spotify preview pill button - plays a 30-second audio preview
 * Styled as a green pill/badge for use in modals
 */
export const SpotifyPreviewPill = memo(function SpotifyPreviewPill({
	spotifyId,
	artistName,
	className,
}: SpotifyPreviewPillProps) {
	const [isPlaying, setIsPlaying] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [previewUrl, setPreviewUrl] = useState<string | null>(null);
	const [noPreview, setNoPreview] = useState(false);

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			if (spotifyId && spotifyAudioManager.isPlaying(spotifyId)) {
				spotifyAudioManager.stop();
			}
		};
	}, [spotifyId]);

	const fetchTopTrack = useCallback(async () => {
		if (!spotifyId || previewUrl || isLoading) return previewUrl;

		setIsLoading(true);
		try {
			const result = await getArtistTopTracksAction({
				spotifyId,
				artistName,
				limit: 1,
			});

			if (result.success && result.data?.[0]?.previewUrl) {
				setPreviewUrl(result.data[0].previewUrl);
				return result.data[0].previewUrl;
			}
			setNoPreview(true);
			return null;
		} catch {
			setNoPreview(true);
			return null;
		} finally {
			setIsLoading(false);
		}
	}, [spotifyId, artistName, previewUrl, isLoading]);

	const handleClick = useCallback(
		async (e: React.MouseEvent) => {
			e.stopPropagation();
			e.preventDefault();

			if (isLoading || !spotifyId) return;

			// If playing, stop
			if (isPlaying) {
				spotifyAudioManager.stop();
				return;
			}

			// Fetch and play
			const url = previewUrl || (await fetchTopTrack());
			if (!url) return;

			spotifyAudioManager.play(spotifyId, url, setIsPlaying);
		},
		[isPlaying, isLoading, previewUrl, fetchTopTrack, spotifyId],
	);

	// Don't render if no spotify ID or no preview available
	if (!spotifyId || noPreview) {
		return null;
	}

	return (
		<button
			type="button"
			onClick={handleClick}
			disabled={isLoading}
			className={cn(
				"inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs transition-all duration-200",
				"bg-[#1DB954] text-white",
				"hover:bg-[#1ed760]",
				"active:scale-95",
				"disabled:cursor-not-allowed",
				className,
			)}
		>
			{isLoading ? (
				<div className="h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent" />
			) : isPlaying ? (
				<RiStopFill className="h-3 w-3" />
			) : (
				<RiPlayFill className="h-3 w-3" />
			)}
			<span>{isPlaying ? "Stop" : "Play"}</span>
		</button>
	);
});

SpotifyPreviewPill.displayName = "SpotifyPreviewPill";
